import { supabase } from './supabase';
import type { Notification } from '@/types/database';

export async function getNotifications() {
  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: notifications, error } = await supabase
      .from('notifications')
      .select(`
        *,
        sender:profiles!notifications_sender_id_fkey(*)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return notifications || [];
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}

export async function getUnreadNotificationsCount() {
  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching unread notifications count:', error);
    return 0;
  }
}

export async function markNotificationsAsRead(notificationIds?: string[]) {
  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.rpc('mark_notifications_as_read', { 
      notification_ids: notificationIds,
      conversation_uuid: null
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error marking notifications as read:', error);
  }
}

export async function markConversationNotificationsAsRead(conversationId: string) {
  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.rpc('mark_notifications_as_read', { 
      notification_ids: null,
      conversation_uuid: conversationId
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error marking conversation notifications as read:', error);
  }
}

export async function toggleEmailNotifications() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get current email notification setting
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('email_notifications')
      .eq('id', user.id)
      .single();

    if (profileError) throw profileError;

    // Toggle the setting
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        email_notifications: !profile.email_notifications,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return !profile.email_notifications;
  } catch (error) {
    console.error('Error toggling email notifications:', error);
    return false;
  }
}

export function subscribeToNotifications(callback: (notification: Notification) => void) {
  return supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications'
      },
      (payload) => {
        callback(payload.new as Notification);
      }
    )
    .subscribe();
}